import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SuggestButton } from "@/components/SuggestBox";
import { api } from "@/lib/api";
import { LayoutDashboard, ListTodo, Users, UserCog, LogOut, Receipt, CalendarClock, Cpu, FileSignature, ClipboardList, FileText, TrendingUp, PenTool } from "lucide-react";

const items = [
  { to: "/", label: "MI Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/tasks", label: "Tasks", icon: ListTodo, tid: "nav-tasks" },
  { to: "/clients", label: "Clients", icon: Users, tid: "nav-clients" },
  { to: "/sales", label: "Sales", icon: TrendingUp, tid: "nav-sales", sales: true },
  { to: "/onboarding", label: "Onboarding", icon: FileSignature, tid: "nav-onboarding", onboarding: true },
  { to: "/fbsign", label: "FBSign", icon: PenTool, tid: "nav-fbsign", fbsign: true },
  { to: "/templates", label: "Templates", icon: FileText, tid: "nav-templates" },
  { to: "/compliance", label: "Compliance", icon: CalendarClock, tid: "nav-compliance" },
  { to: "/finance", label: "Finance & Billing", icon: Receipt, tid: "nav-finance" },
  { to: "/frtx", label: "FRTX", icon: Cpu, tid: "nav-frtx", frtx: true },
  { to: "/team", label: "Users", icon: UserCog, tid: "nav-team" },
  { to: "/build-log", label: "Build Log", icon: ClipboardList, tid: "nav-build-log", buildlog: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [ver, setVer] = useState(null);
  const [pending, setPending] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    api.get("/version").then(r => setVer(r.data)).catch(() => setVer(null));
  }, []);
  useEffect(() => {
    if (!(user?.role === "admin" || user?.buildlog_enabled === true)) return;
    api.get("/build-log/suggestions/count").then(r => setPending(r.data.pending || 0)).catch(() => {});
  }, [user?.role, user?.buildlog_enabled, loc.pathname]);
  return (
    <div className="flex min-h-screen">
      <aside className="w-44 shrink-0 bg-[var(--surface)] border-r border-[var(--line)] flex flex-col" data-testid="sidebar">
        <div className="px-3 pt-5 pb-4 border-b border-[var(--line)]">
          <img
            src="/brand/logo.png"
            alt="Finchley Business Services"
            data-testid="brand-logo"
            className="h-8 w-auto mb-2"
          />
          <div className="overline text-[10px]">Practice OS</div>
        </div>
        <nav className="py-3 flex-1">
          {items.filter(it => {
            if (it.adminOnly && user?.role !== "admin") return false;
            if (it.frtx && !(user?.role === "admin" || user?.frtx_enabled === true)) return false;
            if (it.sales && !(user?.role === "admin" || user?.sales_enabled === true)) return false;
            if (it.onboarding && !(user?.role === "admin" || user?.onboarding_enabled === true)) return false;
            if (it.fbsign && !(user?.role === "admin" || user?.fbsign_enabled === true)) return false;
            if (it.buildlog && !(user?.role === "admin" || user?.buildlog_enabled === true)) return false;
            return true;
          }).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.tid}
              title={it.label}
              className={({ isActive }) => `nav-item nav-item-slim ${isActive ? "active" : ""}`}
            >
              <it.icon size={15} strokeWidth={1.5} />
              <span className="truncate">{it.label}</span>
              {it.to === "/build-log" && pending > 0 && (
                <span data-testid="nav-suggestions-badge" title={`${pending} suggestion(s) waiting`}
                  className="ml-auto text-[9px] font-mono-data px-1 rounded-sm bg-[var(--gold)] text-[var(--navy)]">{pending}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[var(--line)] p-3">
          <SuggestButton />
          <div className="text-xs text-[var(--ink-2)] truncate" title={user?.name}>{user?.name}</div>
          <div className="text-[10px] font-mono-data uppercase tracking-wider text-[var(--ink-2)] mt-0.5">{user?.role}</div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-2 flex items-center gap-1.5 text-xs text-[var(--ink-2)] hover:text-[var(--ink)] transition"
          >
            <LogOut size={13} strokeWidth={1.5} /> Sign out
          </button>
          {ver && (
            <div className="mt-3 pt-2 border-t border-[var(--line)] text-[9px] font-mono-data uppercase tracking-wider text-[var(--ink-2)]" data-testid="app-version" title={`Released ${ver.released_at}${ver.notes ? " — " + ver.notes : ""}`}>
              v{ver.version} <span className="opacity-70">· {ver.released_at}</span>
            </div>
          )}
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
